import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, deleteDoc, doc, where, addDoc, onSnapshot, limit } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseconfig';
import { useAuth } from '../contexts/AuthContext';
import { utils as xlsxUtils, writeFile as xlsxWriteFile } from 'xlsx';
import { fetchCategories } from '../utils/categories';
import { 
  FaPlus, 
  FaTrash, 
  FaQuestion, 
  FaUtensils,
  FaGraduationCap,
  FaShoppingBasket,
  FaLightbulb,
  FaTint,
  FaUsers,
  FaCoffee,
  FaWineGlass,
  FaWater,
  FaToolbox,
  FaHandHoldingWater,
  FaHandHoldingHeart,
  FaSort,
  FaSearch
} from 'react-icons/fa';
import './Settings.css';

const Settings = () => {
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newCategory, setNewCategory] = useState({ 
    name: '', 
    color: '#4ECDC4',
    icon: 'FaQuestion'
  });
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [customCategories, setCustomCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [showIconPicker, setShowIconPicker] = useState(false);
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  const availableIcons = [
    { name: 'FaQuestion', icon: <FaQuestion /> },
    { name: 'FaUtensils', icon: <FaUtensils /> },
    { name: 'FaGraduationCap', icon: <FaGraduationCap /> },
    { name: 'FaShoppingBasket', icon: <FaShoppingBasket /> },
    { name: 'FaLightbulb', icon: <FaLightbulb /> },
    { name: 'FaTint', icon: <FaTint /> },
    { name: 'FaUsers', icon: <FaUsers /> },
    { name: 'FaCoffee', icon: <FaCoffee /> },
    { name: 'FaWineGlass', icon: <FaWineGlass /> },
    { name: 'FaWater', icon: <FaWater /> },
    { name: 'FaToolbox', icon: <FaToolbox /> },
    { name: 'FaHandHoldingWater', icon: <FaHandHoldingWater /> },
    { name: 'FaHandHoldingHeart', icon: <FaHandHoldingHeart /> }
  ];

  // Fetch custom categories
  useEffect(() => {
    if (!currentUser) return;

    const q = query(
      collection(db, 'categories'),
      where('userId', '==', currentUser.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const categories = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCustomCategories(categories);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Fetch all categories for export name mapping
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const fetched = await fetchCategories();
        setAllCategories(fetched);
      } catch (e) {
        console.error('Error loading categories for export:', e);
      }
    };
    loadCategories();
  }, []);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.body.classList.toggle('dark-mode');
    localStorage.setItem('darkMode', !darkMode);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount);
  };

  const exportData = async () => {
    try {
      setIsExporting(true);
      setError('');
      
      // Fetch user's expenses
      const expensesSnapshot = await getDocs(
        query(
          collection(db, 'expenses'),
          where('userId', '==', currentUser.uid),
          orderBy('createdAt', 'desc')
        )
      );
      
      // Transform expenses data for Excel
      const expenses = expensesSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          'Date': formatDate(data.date || data.createdAt),
          'Title': data.title,
          'Category': (allCategories.find(c => c.id === data.category)?.name) || 'Other',
          'Amount': formatAmount(data.amount),
          'Description': data.description || '',
          'Payment Method': data.paymentMethod || 'Cash',
          'Created At': formatDate(data.createdAt)
        };
      });

      // Create workbook and worksheet
      const wb = xlsxUtils.book_new();
      const ws = xlsxUtils.json_to_sheet(expenses);

      // Set column widths
      const columnWidths = [
        { wch: 15 },  // Date
        { wch: 30 },  // Title
        { wch: 20 },  // Category
        { wch: 15 },  // Amount
        { wch: 40 },  // Description
        { wch: 15 },  // Payment Method
        { wch: 15 }   // Created At
      ];
      ws['!cols'] = columnWidths;

      // Add the worksheet to the workbook
      xlsxUtils.book_append_sheet(wb, ws, 'Expenses');

      // Generate filename with current date
      const fileName = `expenses-${new Date().toISOString().split('T')[0]}.xlsx`;

      // Save the file
      xlsxWriteFile(wb, fileName);

      setSuccess('Data exported successfully!');
    } catch (err) {
      console.error('Error exporting data:', err);
      setError('Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      setError('Failed to log out');
    }
  };

  const deleteAccount = async () => {
    if (!window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      return;
    }

    try {
      setIsDeleting(true);
      setError('');

      // Delete user's expenses
      const expensesSnapshot = await getDocs(
        query(
          collection(db, 'expenses'),
          where('userId', '==', currentUser.uid)
        )
      );
      
      const deletePromises = expensesSnapshot.docs.map(doc => 
        deleteDoc(doc.ref)
      );
      
      await Promise.all(deletePromises);
      
      // Delete user document
      await deleteDoc(doc(db, 'users', currentUser.uid));
      
      // Delete user authentication (this would be handled by Firebase Auth)
      // await deleteUser(user);
      
      await logout();
      navigate('/signup');
    } catch (err) {
      console.error('Error deleting account:', err);
      setError('Failed to delete account. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.name.trim()) {
              setError('Item name is required');
      return;
    }

    try {
      setIsAddingCategory(true);
      setError('');
      const proposedName = newCategory.name.trim();
      const proposedNameLower = proposedName.toLowerCase();

      // Client-side duplicate check against already loaded categories (case-insensitive)
      const existsLocally = customCategories.some(c => (c.name || '').trim().toLowerCase() === proposedNameLower);
      if (existsLocally) {
        setError('An item with this name already exists.');
        return;
      }

      // Server-side duplicate check to avoid race conditions
      const dupSnapshot = await getDocs(
        query(
          collection(db, 'categories'),
          where('userId', '==', currentUser.uid),
          where('nameLower', '==', proposedNameLower),
          limit(1)
        )
      );
      if (!dupSnapshot.empty) {
        setError('An item with this name already exists.');
        return;
      }

      //const categoryId = newCategory.name.toLowerCase().replace(/\s+/g, '_');
      const categoryData = {
        name: proposedName,
        nameLower: proposedNameLower,
        color: newCategory.color,
        icon: newCategory.icon,
        userId: currentUser.uid,
        createdAt: new Date().toISOString()
      };

      await addDoc(collection(db, 'categories'), categoryData);

              setSuccess('Item added successfully!');
      setNewCategory({ name: '', color: '#4ECDC4', icon: 'FaQuestion' });
      setShowIconPicker(false);
    } catch (err) {
      console.error('Error adding category:', err);
              setError('Failed to add item. Please try again.');
    } finally {
      setIsAddingCategory(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    console.log("category: " + categoryId)
          if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      setError('');
      // Block deletion if any expenses reference this category
      const expensesUsingCategory = await getDocs(
        query(
          collection(db, 'expenses'),
          where('category', '==', categoryId),
          limit(1)
        )
      );

      if (!expensesUsingCategory.empty) {
        setError('Cannot delete item because it has expenses attached.');
        return;
      }

      await deleteDoc(doc(db, 'categories', categoryId));
              setSuccess('Item deleted successfully!');
    } catch (err) {
      console.error('Error deleting category:', err);
              setError('Failed to delete item. Please try again.');
    }
  };

  const getIconComponent = (iconName) => {
    const icon = availableIcons.find(i => i.name === iconName);
    return icon ? icon.icon : <FaQuestion />;
  };

  const filteredAndSortedCategories = () => {
    let filtered = [...customCategories];
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(category => 
        category.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'date':
          return new Date(b.createdAt) - new Date(a.createdAt);
        default:
          return 0;
      }
    });
    
    return filtered;
  };

  return (
    <div className="settings-container">
      <h2>Settings</h2>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="settings-content">
        <div className="settings-section">
          <h3>Appearance</h3>
          <div className="settings-group">
            <label className="toggle-label">
              <span>Dark Mode</span>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  checked={darkMode}
                  onChange={toggleDarkMode}
                />
                <span className="toggle-slider"></span>
              </div>
            </label>
          </div>
        </div>

        <div className="settings-section">
          <h3>Categories</h3>
          
          <div className="category-controls">
            <div className="search-sort-controls">
              <div className="search-box">
                <FaSearch />
                <input
                  type="text"
                  placeholder="Search categories..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="sort-select"
              >
                <option value="name">Sort by Name</option>
                <option value="date">Sort by Date</option>
              </select>
            </div>
          </div>

          <div className="categories-list">
            {filteredAndSortedCategories().map((category) => (
              <div key={category.id} className="category-item">
                <div className="category-info">
                  <span className="category-icon" style={{ color: category.color }}>
                    {getIconComponent(category.icon)}
                  </span>
                  <span className="category-name">{category.name}</span>
                </div>
                <div className="category-actions">
                  <button
                    className="delete-button"
                    onClick={() => handleDeleteCategory(category.id)}
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          <div className="add-category-form">
            <h4>Add New Item</h4>
            <div className="form-group">
              <input
                type="text"
                                  placeholder="Item Name"
                value={newCategory.name}
                onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
              />
              <input
                type="color"
                value={newCategory.color}
                onChange={(e) => setNewCategory({ ...newCategory, color: e.target.value })}
              />
              <div className="icon-picker">
                <button
                  className="icon-picker-button"
                  onClick={() => setShowIconPicker(!showIconPicker)}
                >
                  {getIconComponent(newCategory.icon)}
                </button>
                {showIconPicker && (
                  <div className="icon-picker-dropdown">
                    {availableIcons.map((icon) => (
                      <button
                        key={icon.name}
                        className="icon-option"
                        onClick={() => {
                          setNewCategory({ ...newCategory, icon: icon.name });
                          setShowIconPicker(false);
                        }}
                      >
                        {icon.icon}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                className="add-category-button"
                        onClick={handleAddCategory}
        disabled={isAddingCategory}
      >
        {isAddingCategory ? 'Adding...' : 'Add Item'}
              </button>
            </div>
          </div>
        </div>

        <div className="settings-section">
          <h3>Data Management</h3>
          <div className="settings-actions">
            <button 
              className="export-button"
              onClick={exportData}
              disabled={isExporting}
            >
              {isExporting ? 'Exporting...' : 'Export to Excel'}
            </button>
            <p className="help-text">
              Download your expense data in Excel format
            </p>
          </div>
        </div>

        <div className="settings-section">
          <h3>Account</h3>
          <div className="settings-actions">
            <button 
              className="logout-button"
              onClick={handleLogout}
            >
              Log Out
            </button>
          </div>
        </div>

        <div className="settings-section danger-zone">
          <h3>Danger Zone</h3>
          <div className="settings-actions">
            <button 
              className="delete-button"
              onClick={deleteAccount}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete Account'}
            </button>
            <p className="help-text warning-text">
              This action cannot be undone. All your data will be permanently deleted.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings; 